import type {
  CmmdBoardTask,
  CmmdMember,
  CmmdProjectAgentMember,
  ScopedProjectRef,
  ScopedThreadRef,
} from "@cmmd-forge/contracts";
import type { SidebarThreadSummary } from "~/types";
import { ProjectMilestoneManagerDialog } from "./ProjectMilestoneManagerDialog";
import { ProjectTaskCreateDialog } from "./ProjectTaskCreateDialog";
import { ProjectTaskDetail, type ProjectTaskWorkbenchDataProps } from "./ProjectTaskDetail";
import type { ProjectTaskBoard } from "./useProjectTaskBoard";
import type { ProjectTaskChrome } from "./useProjectTaskChrome";

export interface ProjectTaskSurfaceDialogsProps {
  readonly chrome: ProjectTaskChrome;
  readonly board: ProjectTaskBoard;
  readonly projectTitle: string;
  readonly projectId: number;
  readonly projectRef?: ScopedProjectRef | null;
  readonly projectIconUrl: string | null;
  readonly projectIcon: string | null;
  readonly tasks: readonly CmmdBoardTask[];
  readonly openTask: CmmdBoardTask | null;
  readonly members: readonly CmmdMember[];
  readonly agentMembers: readonly CmmdProjectAgentMember[];
  readonly agentMembersLoading: boolean;
  readonly agentMembersError: string | null;
  readonly threadSummaries: readonly SidebarThreadSummary[];
  readonly startingTaskId: number | null;
  readonly conversationTaskIds: ReadonlySet<number>;
  readonly workbenchData?: ProjectTaskWorkbenchDataProps;
  readonly onCloseTask: () => void;
  readonly onOpenTask: (taskId: number) => void;
  readonly onStartWork: (task: CmmdBoardTask) => Promise<boolean>;
  readonly onOpenThread: (thread: SidebarThreadSummary) => void;
  readonly onArchiveTask: (taskId: number) => Promise<boolean>;
  readonly onProjectLeadThreadReady?: ((threadRef: ScopedThreadRef) => void) | undefined;
}

export function ProjectTaskSurfaceDialogs(props: ProjectTaskSurfaceDialogsProps) {
  return (
    <>
      <ProjectTaskCreateDialog
        open={props.chrome.composerOpen}
        projectTitle={props.projectTitle}
        projectId={props.projectId}
        projectRef={props.projectRef ?? null}
        target={props.chrome.composerTarget}
        tasks={props.tasks}
        members={props.members}
        agentMembers={props.agentMembers}
        agentMembersLoading={props.agentMembersLoading}
        agentMembersError={props.agentMembersError}
        milestones={props.chrome.milestones.milestones}
        creating={props.board.creating}
        workflowLoading={props.board.loading}
        workflowMutationBlocked={props.board.workflowMutationBlocked}
        error={props.board.mutationError}
        workflow={props.board.workflow}
        onOpenChange={props.chrome.setComposerOpen}
        onCreate={props.board.createTask}
        onProjectLeadThreadReady={props.onProjectLeadThreadReady}
        onTaskTreeCommitted={() => void props.board.reload()}
      />
      <ProjectTaskDetail
        task={props.openTask}
        projectTitle={props.projectTitle}
        projectIconUrl={props.projectIconUrl}
        projectIcon={props.projectIcon}
        allTasks={props.tasks}
        members={props.members}
        agentMembers={props.agentMembers}
        agentMembersLoading={props.agentMembersLoading}
        agentMembersError={props.agentMembersError}
        threadSummaries={props.threadSummaries}
        pending={props.openTask ? props.board.pendingTaskIds.has(props.openTask.id) : false}
        statusMutationDisabled={props.board.workflowMutationBlocked}
        creating={props.board.creating}
        mutationError={props.board.mutationError}
        startingTaskId={props.startingTaskId}
        hasKnownConversation={
          props.openTask ? props.conversationTaskIds.has(props.openTask.id) : false
        }
        workflow={props.board.workflow}
        onClose={props.onCloseTask}
        onUpdate={props.board.updateTask}
        onSetAssignment={props.board.setAssignment}
        onCreateTask={props.board.createTask}
        onSetStatus={props.board.setStatus}
        onStartWork={props.onStartWork}
        onOpenThread={props.onOpenThread}
        onOpenTask={props.onOpenTask}
        onArchive={props.onArchiveTask}
        onUnarchive={props.board.unarchive}
        {...(props.workbenchData ? { workbenchData: props.workbenchData } : {})}
      />
      <ProjectMilestoneManagerDialog
        open={props.chrome.milestoneManagerOpen}
        initialMilestoneId={props.chrome.milestoneManagerTargetId}
        onOpenChange={props.chrome.setMilestoneManagerOpen}
        controller={props.chrome.milestones}
      />
    </>
  );
}
